import { supabase } from "@/integrations/supabase/client";

export interface BriefBulletsAfrikaTask {
  id: string;
  task_type: string;
  schedule_time: string;
  last_run: string | null;
  next_run: string | null;
  status: string;
  retry_count: number;
  error_message: string | null;
  config: {
    photo_url?: string;
    retry_time?: string;
    max_retries?: number;
    notification_email?: string;
    day_of_week?: number;
  } | null;
}

export interface TaskLog {
  id: string;
  task_type: string;
  status: string;
  created_at: string;
  execution_time_ms: number | null;
  message: string | null;
  metadata: any;
}

export interface BriefBulletsAfrikaArticle {
  id: string;
  title: string;
  interviewee_name: string;
  answer: string;
  timestamp: string;
  photo_url: string | null;
  publication_status: string;
}

export class BriefBulletsAfrikaService {
  static async getTaskStatus(): Promise<BriefBulletsAfrikaTask | null> {
    const { data, error } = await supabase
      .from("scheduled_tasks")
      .select("*")
      .eq("task_type", "briefr_bullets_afrika")
      .single();

    if (error) {
      console.error("Error fetching Afrika task status:", error);
      return null;
    }

    return data as BriefBulletsAfrikaTask;
  }

  static async getThisWeekStatus(): Promise<{
    published: boolean;
    article?: BriefBulletsAfrikaArticle;
  }> {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay() + 1);
    startOfWeek.setHours(0, 0, 0, 0);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 7);

    const { data, error } = await supabase
      .from("interviews")
      .select("*")
      .eq("title", "Briefr Bullets: Hva skjer i Afrika denne uken?")
      .gte("timestamp", startOfWeek.toISOString())
      .lt("timestamp", endOfWeek.toISOString())
      .order("timestamp", { ascending: false })
      .limit(1);

    if (error) {
      console.error("Error checking this week's Afrika publication:", error);
      return { published: false };
    }

    if (data && data.length > 0) {
      return {
        published: true,
        article: data[0] as BriefBulletsAfrikaArticle,
      };
    }

    return { published: false };
  }

  static async triggerManualPublish(): Promise<{
    success: boolean;
    articleId?: string;
    error?: string;
  }> {
    try {
      const { data, error } = await supabase.functions.invoke(
        "publish-briefr-bullets-afrika",
        {
          body: { manual: true },
        }
      );

      if (error) {
        return {
          success: false,
          error: error.message,
        };
      }

      if (!data) {
        return {
          success: false,
          error: "Ingen respons fra serveren",
        };
      }

      if (data.success === false) {
        return {
          success: false,
          error: data.message || data.error || "Ukjent feil fra serveren",
        };
      }

      return {
        success: data.success,
        articleId: data.articleId,
        error: data.error,
      };
    } catch (error: any) {
      console.error("Error triggering manual Afrika publish:", error);
      return {
        success: false,
        error: error.message || "Unknown error occurred",
      };
    }
  }

  static async getPublishHistory(
    weeks: number = 4
  ): Promise<BriefBulletsAfrikaArticle[]> {
    const weeksAgo = new Date();
    weeksAgo.setDate(weeksAgo.getDate() - weeks * 7);

    const { data, error } = await supabase
      .from("interviews")
      .select("*")
      .eq("title", "Briefr Bullets: Hva skjer i Afrika denne uken?")
      .gte("timestamp", weeksAgo.toISOString())
      .order("timestamp", { ascending: false });

    if (error) {
      console.error("Error fetching Afrika publish history:", error);
      return [];
    }

    return (data || []) as BriefBulletsAfrikaArticle[];
  }

  static async getTaskLogs(limit: number = 10): Promise<TaskLog[]> {
    const { data, error } = await supabase
      .from("task_logs")
      .select("*")
      .eq("task_type", "briefr_bullets_afrika")
      .order("created_at", { ascending: false })
      .limit(limit);

    if (error) {
      console.error("Error fetching Afrika task logs:", error);
      return [];
    }

    return (data || []) as TaskLog[];
  }

  static async toggleAutomaticPublishing(enable: boolean): Promise<boolean> {
    const { error } = await supabase
      .from("scheduled_tasks")
      .update({ status: enable ? "active" : "paused" })
      .eq("task_type", "briefr_bullets_afrika");

    if (error) {
      console.error("Error toggling Afrika automatic publishing:", error);
      return false;
    }

    return true;
  }

  static async deleteThisWeekArticle(): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const weekStatus = await this.getThisWeekStatus();

      if (!weekStatus.published || !weekStatus.article) {
        return {
          success: false,
          error: "No article found for this week",
        };
      }

      const { error } = await supabase
        .from("interviews")
        .delete()
        .eq("id", weekStatus.article.id);

      if (error) throw error;

      return { success: true };
    } catch (error: any) {
      console.error("Error deleting this week's Afrika article:", error);
      return {
        success: false,
        error: error.message || "Unknown error occurred",
      };
    }
  }

  static async updatePhotoUrl(photoUrl: string): Promise<void> {
    const { error } = await supabase.rpc(
      "update_briefr_bullets_afrika_config",
      {
        new_photo_url: photoUrl,
      }
    );

    if (error) throw error;
  }
}
